import { useCallback, useEffect, useMemo, useState } from "react";
import { format, isValid, parseISO } from "date-fns";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { History, RefreshCw, ChevronLeft, ChevronRight, Eye } from "lucide-react";
import { toastApiError } from "@/lib/toastApiError";
import { cn } from "@/lib/utils";
import {
  ACTIVITY_LOG_FETCH_TYPES,
  ACTIVITY_LOGS_PAGE_SIZE,
  fetchActivityLogs,
  type ActivityLogFetchType,
} from "@/lib/activityLogsApi";
import { fetchLexiconDirectoryUsers } from "@/lib/lexiconUsersApi";

const ALL_FILTER = "__all__";
const PAGE_SIZE = ACTIVITY_LOGS_PAGE_SIZE;
const SEARCH_DEBOUNCE_MS = 400;

function normalizeIsoLike(raw: string): string {
  let t = raw.trim();
  if (/[+-]\d{4}$/.test(t) && !/[+-]\d{2}:\d{2}$/.test(t)) {
    t = t.replace(/([+-])(\d{2})(\d{2})$/, "$1$2:$3");
  }
  if (t.endsWith("+0000")) {
    t = `${t.slice(0, -5)}Z`;
  }
  return t;
}

function parseRowDate(raw: unknown): Date | null {
  if (raw == null) {
    return null;
  }
  const s = String(raw).trim();
  if (!s) {
    return null;
  }
  const normalized = normalizeIsoLike(s);
  const fromIso = parseISO(normalized);
  if (isValid(fromIso)) {
    return fromIso;
  }
  const fallback = new Date(s);
  return isValid(fallback) ? fallback : null;
}

function formatActivityTimestamp(raw: unknown): string {
  const d = parseRowDate(raw);
  if (!d) {
    return "—";
  }
  return format(d, "dd MMM yyyy, hh:mm a");
}

function getActivityType(row: Record<string, unknown>): string {
  const v =
    row.activity_type ?? row.event_type ?? row.action ?? row.type ?? "";
  if (typeof v === "string") {
    return v.trim() || "—";
  }
  if (typeof v === "number" || typeof v === "boolean") {
    return String(v);
  }
  return "—";
}

function getTitle(row: Record<string, unknown>): string {
  const v =
    row.activity_title ??
    row.title ??
    row.message ??
    row.description ??
    row.detail ??
    "";
  if (typeof v === "string") {
    return v.trim() || "—";
  }
  return "—";
}

function getUserDisplay(row: Record<string, unknown>): string {
  const candidates = [
    row.user_name,
    row.user,
    row.user_email,
    row.email,
    row.actor,
    row.username,
  ];
  for (const c of candidates) {
    if (typeof c === "string" && c.trim()) {
      return c.trim();
    }
  }
  return "—";
}

function getIsActive(row: Record<string, unknown>): boolean | null {
  if (row.is_active === true) {
    return true;
  }
  if (row.is_active === false) {
    return false;
  }
  return null;
}

function activityTypeBadgeClass(type: string): string {
  const t = type.trim().toLowerCase();
  if (t === "edit") {
    return "border-purple-500/40 bg-purple-500/12 font-medium text-purple-950 dark:text-purple-100";
  }
  if (t === "delete") {
    return "border-orange-500/45 bg-orange-500/12 font-medium text-orange-950 dark:text-orange-100";
  }
  if (t === "create") {
    return "border-teal-500/40 bg-gradient-to-br from-emerald-500/15 to-sky-500/12 font-medium text-emerald-950 dark:text-emerald-50";
  }
  return "border-border bg-muted/50 text-foreground";
}

function formatDetailValue(value: unknown): string {
  if (value == null) {
    return "—";
  }
  if (
    typeof value === "string" ||
    typeof value === "number" ||
    typeof value === "boolean"
  ) {
    return String(value);
  }
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
}

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

function isUuidLike(s: string): boolean {
  return UUID_RE.test(s.trim());
}

/** Map `user_id` / profile `id` (lowercase) → display label from directory GET. */
function buildUserDirectoryMap(
  entries: Iterable<{ id: string; user_id: string; email: string | null; display_name: string | null }>
): Map<string, string> {
  const m = new Map<string, string>();
  for (const u of entries) {
    const label =
      u.display_name?.trim() ||
      u.email?.trim() ||
      u.user_id.trim() ||
      u.id.trim();
    if (!label) {
      continue;
    }
    m.set(u.user_id.trim().toLowerCase(), label);
    if (u.id.trim()) {
      m.set(u.id.trim().toLowerCase(), label);
    }
  }
  return m;
}

function resolveCreatorUpdaterLabel(
  rawUuid: string,
  field: "created_by" | "updated_by",
  row: Record<string, unknown>,
  directory: Map<string, string>
): string {
  const uuid = rawUuid.trim();
  const lower = uuid.toLowerCase();

  const fromDir = directory.get(lower);
  if (fromDir) {
    return fromDir;
  }

  const actorName =
    typeof row.user_name === "string" ? row.user_name.trim() : "";
  const createdBy =
    typeof row.created_by === "string"
      ? row.created_by.trim().toLowerCase()
      : "";
  const updatedBy =
    typeof row.updated_by === "string"
      ? row.updated_by.trim().toLowerCase()
      : "";

  if (actorName && createdBy && updatedBy && createdBy === updatedBy && lower === createdBy) {
    return actorName;
  }
  if (actorName && field === "created_by" && lower === createdBy) {
    return actorName;
  }
  if (actorName && field === "updated_by" && lower === updatedBy) {
    return actorName;
  }

  return uuid;
}

function formatDetailField(
  key: string,
  value: unknown,
  row: Record<string, unknown>,
  directory: Map<string, string>
): string {
  if (
    (key === "created_by" || key === "updated_by") &&
    typeof value === "string" &&
    value.trim() &&
    isUuidLike(value)
  ) {
    return resolveCreatorUpdaterLabel(value, key, row, directory);
  }
  return formatDetailValue(value);
}

function rowKey(row: Record<string, unknown>, index: number): string {
  if (typeof row.id === "string") {
    return row.id;
  }
  if (typeof row.uuid === "string") {
    return row.uuid;
  }
  if (typeof row.id === "number" && Number.isFinite(row.id)) {
    return String(row.id);
  }
  return `row-${index}`;
}

/** Hidden in the View/details sheet only (still used internally for row keys). */
const DETAIL_SHEET_HIDDEN_KEYS = new Set(["id"]);

/** Preferred order for detail sheet (then remaining keys alpha). */
const DETAIL_KEY_PRIORITY = [
  "activity_type",
  "activity_title",
  "user_name",
  "created_on",
  "created_at",
  "is_active",
  "created_by",
  "updated_by",
];

function orderedDetailEntries(
  row: Record<string, unknown>
): [string, unknown][] {
  const keys = Object.keys(row).filter(k => !DETAIL_SHEET_HIDDEN_KEYS.has(k));
  const priority = DETAIL_KEY_PRIORITY.filter(k => keys.includes(k));
  const rest = keys
    .filter(k => !DETAIL_KEY_PRIORITY.includes(k))
    .sort((a, b) => a.localeCompare(b));
  const ordered = [...priority, ...rest];
  return ordered.map(k => [k, row[k]]);
}

/**
 * Jain Lexicon activity logs from `/jain_lexicon/activity-logs/`.
 */
export function AdminActivityModule() {
  const [logs, setLogs] = useState<Record<string, unknown>[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState(ALL_FILTER);
  const [userFilter, setUserFilter] = useState(ALL_FILTER);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [page, setPage] = useState(1);
  const [detailRow, setDetailRow] = useState<Record<string, unknown> | null>(
    null
  );
  const [userDirectory, setUserDirectory] = useState<Map<string, string>>(
    () => new Map()
  );

  useEffect(() => {
    const timer = window.setTimeout(
      () => setDebouncedSearch(search.trim()),
      SEARCH_DEBOUNCE_MS
    );
    return () => window.clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    void (async () => {
      const { users, error } = await fetchLexiconDirectoryUsers();
      if (!error) {
        setUserDirectory(buildUserDirectoryMap(users));
      }
    })();
  }, []);

  const loadLogs = useCallback(async () => {
    setIsLoading(true);
    setFetchError(null);
    try {
      const activity_type =
        typeFilter !== ALL_FILTER
          ? (typeFilter as ActivityLogFetchType)
          : undefined;
      const { logs: rows, error, pagination } = await fetchActivityLogs({
        page,
        page_size: PAGE_SIZE,
        activity_type,
        search: debouncedSearch || undefined,
        start_date: dateFrom || undefined,
        end_date: dateTo || undefined,
      });
      if (error) {
        setLogs([]);
        setTotalCount(0);
        setFetchError(error);
        toastApiError(error, { id: "jl-activity-logs" });
      } else {
        setLogs(rows);
        setTotalCount(pagination?.count ?? rows.length);
      }
    } catch (e) {
      const msg = e instanceof Error ? e.message : "Failed to load activity logs";
      setFetchError(msg);
      setLogs([]);
      setTotalCount(0);
      toastApiError(msg, { id: "jl-activity-logs" });
    } finally {
      setIsLoading(false);
    }
  }, [page, debouncedSearch, typeFilter, dateFrom, dateTo]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, typeFilter, dateFrom, dateTo]);

  useEffect(() => {
    void loadLogs();
  }, [loadLogs]);

  const typeOptions = useMemo(() => [...ACTIVITY_LOG_FETCH_TYPES], []);

  const userOptions = useMemo(() => {
    const set = new Set<string>();
    for (const label of userDirectory.values()) {
      set.add(label);
    }
    for (const row of logs) {
      const u = getUserDisplay(row);
      if (u !== "—") {
        set.add(u);
      }
    }
    return [...set].sort((a, b) => a.localeCompare(b));
  }, [userDirectory, logs]);

  const displayed = useMemo(() => {
    if (userFilter === ALL_FILTER) {
      return logs;
    }
    return logs.filter(row => getUserDisplay(row) === userFilter);
  }, [logs, userFilter]);

  const apiTotal =
    userFilter === ALL_FILTER ? totalCount : displayed.length;
  const totalPages = Math.max(1, Math.ceil(apiTotal / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const pageStart = (safePage - 1) * PAGE_SIZE;

  useEffect(() => {
    if (page > totalPages) {
      setPage(totalPages);
    }
  }, [page, totalPages]);

  const renderTypeBadge = (type: string) => (
    <Badge
      variant="outline"
      className={cn(
        "max-w-full shrink-0 font-medium capitalize",
        activityTypeBadgeClass(type)
      )}
    >
      <span className="truncate">{type}</span>
    </Badge>
  );

  const renderActiveBadge = (row: Record<string, unknown>) => {
    const a = getIsActive(row);
    if (a === null) {
      return <span className="text-muted-foreground text-sm">—</span>;
    }
    return a ? (
      <Badge
        variant="outline"
        className="border-emerald-500/40 bg-emerald-500/12 font-medium text-emerald-900 dark:text-emerald-100"
      >
        Active
      </Badge>
    ) : (
      <Badge
        variant="outline"
        className="border-border bg-muted/70 font-medium text-muted-foreground"
      >
        Inactive
      </Badge>
    );
  };

  const titleCell = (title: string) => {
    if (title === "—") {
      return <span className="text-muted-foreground">—</span>;
    }
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="text-foreground block max-w-[min(28rem,45vw)] cursor-default truncate text-sm leading-snug md:max-w-[32rem]">
            {title}
          </span>
        </TooltipTrigger>
        <TooltipContent
          side="top"
          className="max-w-md border bg-popover text-left text-sm shadow-md"
        >
          <p className="whitespace-pre-wrap break-words">{title}</p>
        </TooltipContent>
      </Tooltip>
    );
  };

  return (
    <TooltipProvider delayDuration={200}>
      <div className="space-y-6">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-start gap-3">
            <div className="bg-primary/10 flex h-10 w-10 shrink-0 items-center justify-center rounded-lg">
              <History className="text-primary h-5 w-5" />
            </div>
            <div>
              <h2 className="font-serif text-2xl font-bold">Activity module</h2>
              <p className="text-muted-foreground mt-0.5 text-sm">
                Search, dates, and type are filtered by the activity logs API (Edit,
                Delete, Create).
              </p>
            </div>
          </div>
        </div>

        <div className="border-border/60 bg-card/30 space-y-3 rounded-xl border p-4 shadow-sm">
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-6 lg:items-end">
            <div className="space-y-1.5 sm:col-span-2 lg:col-span-2">
              <Label htmlFor="activity-search" className="text-xs font-medium">
                Search
              </Label>
              <Input
                id="activity-search"
                placeholder="Search title, user, type…"
                value={search}
                onChange={e => setSearch(e.target.value)}
                className="h-10"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-medium">Type</Label>
              <Select value={typeFilter} onValueChange={setTypeFilter}>
                <SelectTrigger className="h-10">
                  <SelectValue placeholder="All types" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={ALL_FILTER}>All types</SelectItem>
                  {typeOptions.map(t => (
                    <SelectItem key={t} value={t}>
                      {t}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-medium">User</Label>
              <Select value={userFilter} onValueChange={setUserFilter}>
                <SelectTrigger className="h-10">
                  <SelectValue placeholder="All users" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={ALL_FILTER}>All users</SelectItem>
                  {userOptions.map(u => (
                    <SelectItem key={u} value={u}>
                      {u}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="activity-from" className="text-xs font-medium">
                From
              </Label>
              <Input
                id="activity-from"
                type="date"
                value={dateFrom}
                onChange={e => setDateFrom(e.target.value)}
                className="h-10"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="activity-to" className="text-xs font-medium">
                To
              </Label>
              <div className="flex gap-2">
                <Input
                  id="activity-to"
                  type="date"
                  value={dateTo}
                  onChange={e => setDateTo(e.target.value)}
                  className="h-10 min-w-0 flex-1"
                />
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  className="h-10 w-10 shrink-0"
                  disabled={isLoading}
                  onClick={() => void loadLogs()}
                  title="Refresh"
                >
                  <RefreshCw
                    className={cn("h-4 w-4", isLoading && "animate-spin")}
                  />
                </Button>
              </div>
            </div>
          </div>
        </div>

        {fetchError ? (
          <Alert variant="destructive">
            <AlertTitle>Could not load activity logs</AlertTitle>
            <AlertDescription>{fetchError}</AlertDescription>
          </Alert>
        ) : null}

        <div className="border-border/60 overflow-hidden rounded-xl border bg-white shadow-sm">
          {isLoading ? (
            <div className="text-muted-foreground p-10 text-center text-sm">
              Loading…
            </div>
          ) : displayed.length === 0 ? (
            <div className="text-muted-foreground p-10 text-center text-sm">
              {totalCount === 0 && userFilter === ALL_FILTER
                ? "No activity log entries returned."
                : "No entries match your filters."}
            </div>
          ) : (
            <>
              {/* Desktop table */}
              <div className="hidden md:block">
                <div className="max-w-full overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-muted/30 hover:bg-muted/30">
                        <TableHead className="whitespace-nowrap font-semibold">
                          Time
                        </TableHead>
                        <TableHead className="whitespace-nowrap font-semibold">
                          Type
                        </TableHead>
                        <TableHead className="min-w-[12rem] font-semibold">
                          Title
                        </TableHead>
                        <TableHead className="whitespace-nowrap font-semibold">
                          User
                        </TableHead>
                        <TableHead className="whitespace-nowrap font-semibold">
                          Active
                        </TableHead>
                        <TableHead className="w-[100px] text-right font-semibold">
                          Action
                        </TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {displayed.map((row, i) => {
                        const tRaw =
                          row.created_on ??
                          row.created_at ??
                          row.timestamp;
                        const type = getActivityType(row);
                        const title = getTitle(row);
                        const user = getUserDisplay(row);
                        return (
                          <TableRow
                            key={rowKey(row, pageStart + i)}
                            className="hover:bg-muted/50 transition-colors"
                          >
                            <TableCell className="text-muted-foreground align-middle text-sm whitespace-nowrap tabular-nums">
                              {formatActivityTimestamp(tRaw)}
                            </TableCell>
                            <TableCell className="align-middle">
                              {renderTypeBadge(type)}
                            </TableCell>
                            <TableCell className="align-middle">
                              {titleCell(title)}
                            </TableCell>
                            <TableCell className="max-w-[10rem] truncate align-middle text-sm font-medium">
                              {user}
                            </TableCell>
                            <TableCell className="align-middle">
                              {renderActiveBadge(row)}
                            </TableCell>
                            <TableCell className="text-right align-middle">
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                className="h-8 gap-1.5 px-2.5"
                                onClick={() => setDetailRow(row)}
                              >
                                <Eye className="h-3.5 w-3.5" />
                                View
                              </Button>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              </div>

              {/* Mobile cards */}
              <div className="md:hidden space-y-3 p-4">
                {displayed.map((row, i) => {
                  const tRaw =
                    row.created_on ??
                    row.created_at ??
                    row.timestamp;
                  const type = getActivityType(row);
                  const title = getTitle(row);
                  const user = getUserDisplay(row);
                  return (
                    <article
                      key={rowKey(row, pageStart + i)}
                      className="border-border/60 hover:bg-muted/40 space-y-3 rounded-xl border bg-white p-4 shadow-sm transition-colors"
                    >
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        {renderTypeBadge(type)}
                        {renderActiveBadge(row)}
                      </div>
                      <p className="text-muted-foreground text-xs tabular-nums">
                        {formatActivityTimestamp(tRaw)}
                      </p>
                      <p className="text-sm font-medium leading-snug">{user}</p>
                      <p className="text-foreground line-clamp-3 text-sm leading-snug">
                        {title}
                      </p>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="w-full gap-2"
                        onClick={() => setDetailRow(row)}
                      >
                        <Eye className="h-4 w-4" />
                        View details
                      </Button>
                    </article>
                  );
                })}
              </div>
            </>
          )}
        </div>

        {!isLoading && displayed.length > 0 ? (
          <div className="text-muted-foreground flex flex-col items-center justify-between gap-3 border-t border-transparent pt-2 text-sm sm:flex-row">
            <p>
              Showing{" "}
              <span className="text-foreground font-medium">
                {pageStart + 1}–
                {Math.min(pageStart + displayed.length, apiTotal)}
              </span>{" "}
              of{" "}
              <span className="text-foreground font-medium">{apiTotal}</span>
              {userFilter !== ALL_FILTER ? (
                <span className="ml-1">(user filter on this page)</span>
              ) : null}
            </p>
            <div className="flex items-center gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-9 gap-1"
                disabled={safePage <= 1}
                onClick={() => setPage(p => Math.max(1, p - 1))}
              >
                <ChevronLeft className="h-4 w-4" />
                Previous
              </Button>
              <span className="tabular-nums">
                Page {safePage} of {totalPages}
              </span>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-9 gap-1"
                disabled={safePage >= totalPages}
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              >
                Next
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        ) : null}

        <Sheet
          open={detailRow !== null}
          onOpenChange={open => {
            if (!open) {
              setDetailRow(null);
            }
          }}
        >
          <SheetContent
            side="right"
            className="flex w-full flex-col gap-0 p-0 sm:max-w-xl"
          >
            <SheetHeader className="border-border shrink-0 space-y-1 border-b px-6 py-4 text-left">
              <SheetTitle>Activity details</SheetTitle>
              <SheetDescription className="line-clamp-2">
                {detailRow ? getTitle(detailRow) : ""}
              </SheetDescription>
            </SheetHeader>
            {detailRow ? (
              <ScrollArea className="h-[calc(100vh-8rem)] px-6 py-4">
                <dl className="space-y-0">
                  {orderedDetailEntries(detailRow).map(([key, value]) => {
                    const resolved = formatDetailField(
                      key,
                      value,
                      detailRow,
                      userDirectory
                    );
                    const showUuidTooltip =
                      (key === "created_by" || key === "updated_by") &&
                      typeof value === "string" &&
                      isUuidLike(value) &&
                      resolved.trim() !== value.trim();
                    return (
                      <div
                        key={key}
                        className="border-border/60 grid gap-1 border-b py-3 last:border-0 sm:grid-cols-[minmax(0,7.5rem)_1fr] sm:gap-4"
                      >
                        <dt className="text-muted-foreground text-xs font-semibold uppercase tracking-wide">
                          {key.replace(/_/g, " ")}
                        </dt>
                        <dd
                          className={cn(
                            "text-foreground leading-relaxed break-words",
                            typeof value === "object" && value !== null
                              ? "font-mono text-xs whitespace-pre-wrap"
                              : "font-sans text-sm"
                          )}
                          title={showUuidTooltip ? value : undefined}
                        >
                          {resolved}
                        </dd>
                      </div>
                    );
                  })}
                </dl>
              </ScrollArea>
            ) : null}
          </SheetContent>
        </Sheet>
      </div>
    </TooltipProvider>
  );
}
